/**
 * PATH: packages/email-hub/src/feature/components/OfflinePill.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-OFFLINEPILL-001
 * PURPOSE: Small fixed pill that tells the operator the email hub is
 *          OFFLINE (fed by the browser online/offline events), plus the
 *          useOnlineStatus hook that tracks connectivity.
 * WHY: MR-C offline resilience (audit 2026-07-06): connectivity gating
 *      already existed everywhere (syncs skip while navigator.onLine is
 *      false, transient fetch failures stay silent) but NOTHING told the
 *      user they were offline — failures surfaced as generic error
 *      banners, fuelling the "my emails vanished" perception. The pill
 *      makes the state legible; the durable list cache (useEmails.
 *      listCache) keeps the rows painted underneath it.
 */
import { useEffect, useState } from 'react'
import { WifiOff } from 'lucide-react'

/** Track browser connectivity via the online/offline events.
 *  Defaults to online when the API is unavailable (SSR/tests). */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(
    () => typeof navigator === 'undefined' || navigator.onLine !== false,
  )
  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])
  return online
}

/** Fixed bottom-centre "Offline" pill. Renders nothing while online. */
export function OfflinePill() {
  const online = useOnlineStatus()
  if (online) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-xs font-medium text-amber-700 shadow-lg backdrop-blur dark:text-amber-300"
    >
      <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
      Offline — showing cached emails
    </div>
  )
}
